/**
 * @module authenticationSlice
 * @description
 * Slice de Redux Toolkit para manejar el estado de autenticación del usuario, incluyendo login, logout y el token de acceso.
 */

import { createSlice } from "@reduxjs/toolkit";
import { login } from "./thunks";

/**
 * Slice para manejar autenticación del usuario.
 */
export const authenticationSlice = createSlice({
	name: 'authetication', // ⚠️ Podrías corregir esto a 'authentication'
	initialState: {
		/**
		 * @property {Object|null} user - Información del usuario autenticado, obtenida del localStorage si existe.
		 */
		user: localStorage.getItem("user") !== undefined ? JSON.parse(localStorage.getItem("user")) : null,

		/**
		 * @property {boolean} isLoading - Indica si la solicitud de autenticación está en progreso.
		 */
		isLoading: false,

		/**
		 * @property {any} error - Error devuelto por el proceso de login fallido.
		 */
		error: null
	},

	reducers: {
		/**
		 * @function logout
		 * @description Elimina al usuario del estado y del localStorage.
		 */
		logout: (state) => {
			state.user = null;
			localStorage.removeItem('user');
		},

		/**
		 * @function setTokenAccess
		 * @description Actualiza el token de acceso del usuario tanto en el estado como en el localStorage.
		 * @param {object} state - Estado actual del slice.
		 * @param {object} action - Acción de Redux con el nuevo token como payload.
		 */
		setTokenAccess: (state, action) => {
			state.user.token_access = action.payload;
			localStorage.setItem('user', JSON.stringify(state.user));
		}
	},

	extraReducers: (builder) => {
		builder
			/**
			 * Caso pendiente del thunk `login`.
			 * Marca la carga como activa y limpia errores previos.
			 */
			.addCase(login.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})

			/**
			 * Caso cumplido del thunk `login`.
			 * Guarda el usuario en el estado y en localStorage.
			 */
			.addCase(login.fulfilled, (state, action) => {
				state.isLoading = false;
				const user = action.payload;
				localStorage.setItem('user', JSON.stringify(user));
				state.user = user;
			})

			/**
			 * Caso rechazado del thunk `login`.
			 * Detiene la carga y guarda el error.
			 */
			.addCase(login.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload;
			});
	},
});

/**
 * Acciones exportadas: logout, setTokenAccess
 */
export const { logout, setTokenAccess } = authenticationSlice.actions;